import {
  motion,
  useAnimationFrame,
  useMotionValue,
  useReducedMotion,
} from "framer-motion";
import { useLayoutEffect, useRef, useState } from "react";

import { useThemeContext } from "@/react/providers/Theme";

import aliAbdaal from "./socialProofLogos/aliAbdaal.svg";
import aliAbdaalDark from "./socialProofLogos/aliAbdaalDark.svg";
import goodInside from "./socialProofLogos/goodInside.svg";
import goodInsideDark from "./socialProofLogos/goodInsideDark.svg";
import harvard from "./socialProofLogos/harvard.svg";
import harvardDark from "./socialProofLogos/harvardDark.svg";
import lovable from "./socialProofLogos/lovable.png";
import lovableDark from "./socialProofLogos/lovableDark.png";
import miro from "./socialProofLogos/miro.svg";
import miroDark from "./socialProofLogos/miroDark.svg";
import obama from "./socialProofLogos/obama.svg";
import obamaDark from "./socialProofLogos/obamaDark.svg";
import timFerriss from "./socialProofLogos/timFerriss.svg";
import timFerrissDark from "./socialProofLogos/timFerrissDark.svg";

const LOGOS = [
  { name: "Miro", light: miro, dark: miroDark, width: 80, height: 30 },
  {
    name: "Ali Abdaal",
    light: aliAbdaal,
    dark: aliAbdaalDark,
    width: 128,
    height: 30,
  },
  {
    name: "Harvard University",
    light: harvard,
    dark: harvardDark,
    width: 90,
    height: 24,
  },
  {
    name: "Tim Ferriss",
    light: timFerriss,
    dark: timFerrissDark,
    width: 120,
    height: 30,
  },
  {
    name: "Obama Foundation",
    light: obama,
    dark: obamaDark,
    width: 111,
    height: 26,
  },
  {
    name: "Lovable",
    light: lovable,
    dark: lovableDark,
    width: 111,
    height: 30,
  },
  {
    name: "Good Inside",
    light: goodInside,
    dark: goodInsideDark,
    width: 70,
    height: 30,
  },
];

const GAP = 40;
// One full loop of the marquee: every logo plus its trailing gap.
const SINGLE_SET_WIDTH =
  LOGOS.reduce((sum, logo) => sum + logo.width, 0) + LOGOS.length * GAP;

const SCROLL_SPEED_PX_PER_SECOND = 36;
const EDGE_FADE =
  "[mask-image:linear-gradient(to_right,transparent,black_40px,black_calc(100%-40px),transparent)] [-webkit-mask-image:linear-gradient(to_right,transparent,black_40px,black_calc(100%-40px),transparent)]";

interface LogoRowProps {
  isDarkAppearance?: boolean;
}

const LogoRow = ({ isDarkAppearance }: LogoRowProps) => (
  <div className="flex shrink-0 items-center gap-x-10 pr-10">
    {LOGOS.map(logo => (
      <img
        key={logo.name}
        className="shrink-0"
        src={isDarkAppearance ? logo.dark : logo.light}
        alt={logo.name}
        width={logo.width}
        height={logo.height}
        draggable={false}
      />
    ))}
  </div>
);

export const SocialProof = () => {
  const { isDarkAppearance } = useThemeContext();
  const shouldReduceMotion = useReducedMotion();
  const x = useMotionValue(0);
  const isDraggingRef = useRef(false);
  const viewportRef = useRef<HTMLDivElement>(null);
  const [canFitWithoutScroll, setCanFitWithoutScroll] = useState(false);

  useLayoutEffect(() => {
    const node = viewportRef.current;
    if (!node) return;

    const measure = () => {
      setCanFitWithoutScroll(
        node.getBoundingClientRect().width >= SINGLE_SET_WIDTH,
      );
    };
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const wrap = () => {
    const value = x.get();
    const wrapped =
      (((value % SINGLE_SET_WIDTH) + SINGLE_SET_WIDTH) % SINGLE_SET_WIDTH) -
      SINGLE_SET_WIDTH;
    x.set(wrapped);
  };

  useAnimationFrame((_, delta) => {
    if (canFitWithoutScroll || shouldReduceMotion || isDraggingRef.current) {
      return;
    }
    x.set(x.get() - (SCROLL_SPEED_PX_PER_SECOND * delta) / 1000);
    wrap();
  });

  return (
    <div
      ref={viewportRef}
      className={
        canFitWithoutScroll
          ? "opacity-60"
          : `overflow-hidden opacity-60 ${EDGE_FADE}`
      }
    >
      {canFitWithoutScroll ? (
        <div className="flex items-center justify-between">
          {LOGOS.map(logo => (
            <img
              key={logo.name}
              src={isDarkAppearance ? logo.dark : logo.light}
              alt={logo.name}
              width={logo.width}
              height={logo.height}
              draggable={false}
            />
          ))}
        </div>
      ) : (
        <motion.div
          className="flex w-max cursor-grab items-center active:cursor-grabbing"
          style={{ x }}
          drag="x"
          dragMomentum={false}
          onDragStart={() => {
            isDraggingRef.current = true;
          }}
          onDrag={wrap}
          onDragEnd={() => {
            isDraggingRef.current = false;
          }}
        >
          <LogoRow isDarkAppearance={isDarkAppearance} />
          <LogoRow isDarkAppearance={isDarkAppearance} />
          <LogoRow isDarkAppearance={isDarkAppearance} />
        </motion.div>
      )}
    </div>
  );
};
